import {
  ExportService,
  ExportFileFormat,
  ExportRange,
  ExportContext,
} from "app/layout/export-overlay";
import {
  SUMMARY_MATERIAL_EXPORT_ID,
  SUMMARY_MATERIAL_TITLE,
  SUMMARY_MATERIAL_PROPERTIES,
} from "./summary-material.constants";
import ceil from "lodash-es/ceil";
import {
  buildQuery,
  page,
  seed,
  andAllWhere,
  Ops,
  noop,
  where,
  orderBy,
  CombinedFilter,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { takeUntil } from "rxjs";
import get from "lodash-es/get";
import { PrincipalScreenService } from "app/modules/components/monitor/principal-screen/principal-screen.service";
import { OrderAttributes } from "@albatrosdeveloper/ave-models-package";
import { Injectable } from "@angular/core";
import { DocumentTypeEnum, EXPORT_BATCHES_GROUPED_EXCEL_TYPE_DOCUMENTS } from "app/common/enums/document-types.enum";

@Injectable()
export class SummaryMaterialExport {
  constructor(
    private exportService: ExportService,
    private principalScreenService: PrincipalScreenService
  ) {}

  private transformItem(item: any) {
    const trip = get(item, "trip.id", "");
    const subRouteCode = get(item, "subRoute.code", "");
    const subRouteName = get(item, "subRoute.name", "");
    const materialCode = get(item, "code", "");
    const materialName = get(item, "name", "");
    const orders = get(item, "orders", []);

    // Si la primera orden tiene type.code === 'DT003', no procesar batches
    const firstOrderTypeCode = get(orders, "[0].type.code", "");
    if (firstOrderTypeCode === "DT003") {
      const baseUnitOfMeasureName =  get(item, "baseUnitOfMeasure.name", "");
      const parallelUnitOfMeasureName =  get(item, "pararellUnitOfMeasure.name", "");
      const quantity = parseFloat(get(item, "baseQuantity", 0)) || 0;
      const parallelQuantity =
        parseFloat(get(item, "pararellQuantity", 0)) || 0;

      return [
        {
          trip,
          subRouteCode,
          subRouteName,
          materialCode,
          materialName,
          baseUnitOfMeasureName,
          quantity,
          parallelUnitOfMeasureName,
          parallelQuantity,
        },
      ];
    }

    if (!Array.isArray(orders) || orders.length === 0) {
      return [
        {
          trip,
          subRouteCode,
          subRouteName,
          materialCode,
          materialName,
          batch: "",
          quantity: "",
          parallelQuantity: "",
        },
      ];
    }

    // Mapa para agrupar y sumar cantidades por lote
    const batchMap = new Map<
      string,
      {
        trip: string;
        subRouteCode: string;
        subRouteName: string;
        materialCode: string;
        materialName: string;
        batch: string;
        quantity: number;
        parallelQuantity: number;
      }
    >();

    // Procesar todas las órdenes para agrupar por lote
    orders.forEach((order) => {
      const detail = get(order, "detail", {});
      const batches = get(detail, "batches", []);


      if (batches.length > 0) {
        batches.forEach((batch) => {
          const batchCode = get(batch, "bacth", "");
          const quantity = parseFloat(get(batch, "quantity", 0)) || 0;
          const parallelQuantity =
            parseFloat(get(batch, "parallelQuantity", 0)) || 0;

          if (batchCode) {
            if (batchMap.has(batchCode)) {
              // Si el lote ya existe, sumar las cantidades
              const existing = batchMap.get(batchCode)!;
              existing.quantity += quantity;
              existing.parallelQuantity += parallelQuantity;
            } else {
              // Si es un nuevo lote, crear entrada
              batchMap.set(batchCode, {
                trip,
                subRouteCode,
                subRouteName,
                materialCode,
                materialName,
                batch: batchCode,
                quantity,
                parallelQuantity,
              });
            }
          }
        });
      }
    });

    // Convertir el mapa a array
    const result = Array.from(batchMap.values());

    // Si no se encontraron lotes, retornar una fila vacía
    if (result.length === 0) {
      return [
        {
          trip,
          subRouteCode,
          subRouteName,
          materialCode,
          materialName,
          batch: "",
          quantity: "",
          parallelQuantity: "",
        },
      ];
    }

    return result;
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    type: string
  ) {
    const totalPages = ceil(totalItems / pageSize) + 1;

    this.exportService
      .task({
        id: SUMMARY_MATERIAL_EXPORT_ID,
        title: SUMMARY_MATERIAL_TITLE,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            SUMMARY_MATERIAL_TITLE,
            SUMMARY_MATERIAL_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const currentPage =
            range === ExportRange.AllPages ? ctx.iteration : pageIndex;

          let query;
          if (type?.includes("DT003")) {
            query = buildQuery(
              seed(this.principalScreenService.filterToExportLD),
              page(currentPage, pageSize)
            );
            this.principalScreenService.filterToExportLD = query;
          } else if (type?.includes("DT004") || type?.includes(DocumentTypeEnum.ENTREGA_SALIDA)) {
            query = buildQuery(
              seed(this.principalScreenService.filterToExportES),
              page(currentPage, pageSize)
            );
            this.principalScreenService.filterToExportES = query;
          } else {
            query = buildQuery(page(currentPage, pageSize));
          }

          this.principalScreenService
            .getOrdersGroupedByMaterialExport(query)
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              get(response, "data.0.data", []).map((item) => {
                ctx.processor.addToSection(
                  SUMMARY_MATERIAL_TITLE,
                  this.transformItem(item)
                );
              });
              ctx.next();
            });
        },
      })
      .start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(SUMMARY_MATERIAL_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }

    return true;
  }
}
